import { useApolloClient } from '@apollo/client';
import { useMemo, useRef } from 'react';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import ajax from '@client/components/ajax';
import {
  AdminCreatorsPageQueryVariables,
  AdminCreatorsSortDirection,
  AdminCreatorsSortField,
  useAdminCreatorsPageQuery,
  useAdminUpdateInfluencerMutation,
} from 'generated/graphql';
import {
  removeCreatorFromCache,
  writeCreatorTier,
} from './adminCreatorsPage.cache';
import { toInfluencers } from './InfluencerList.mappers';
import {
  CreatorsPageQuery,
  PagedInfluencerListModel,
} from './InfluencerList.types';

const PAGE_SIZE = 50;

const SORT_FIELDS: Record<
  CreatorsPageQuery['sort']['field'],
  AdminCreatorsSortField
> = {
  name: AdminCreatorsSortField.Name,
  publishedAt: AdminCreatorsSortField.PublishedAt,
  createdAt: AdminCreatorsSortField.CreatedAt,
};

const SORT_DIRECTIONS: Record<
  CreatorsPageQuery['sort']['direction'],
  AdminCreatorsSortDirection
> = {
  asc: AdminCreatorsSortDirection.Asc,
  desc: AdminCreatorsSortDirection.Desc,
};

const toVariables = (
  pageQuery: CreatorsPageQuery,
): AdminCreatorsPageQueryVariables => ({
  filter: {
    search: pageQuery.search.trim() || null,
    rules: pageQuery.rules,
  },
  sort: {
    field: SORT_FIELDS[pageQuery.sort.field],
    direction: SORT_DIRECTIONS[pageQuery.sort.direction],
  },
  offset: 0,
  limit: PAGE_SIZE,
});

export const useAdminCreatorsPageModel = (
  pageQuery: CreatorsPageQuery,
): Async<PagedInfluencerListModel> => {
  const client = useApolloClient();
  const variables = toVariables(pageQuery);
  const query = useAdminCreatorsPageQuery({
    variables,
    fetchPolicy: 'cache-and-network',
  });
  const [updateTierMutation] = useAdminUpdateInfluencerMutation();
  const isLoadingMore = useRef(false);

  const page = query.data?.adminCreatorsPage;
  const influencers = useMemo(
    () => (page ? toInfluencers(page.items) : null),
    [page],
  );

  if (!page || !influencers) {
    if (query.error) return { type: 'fetch-error', value: query.error.message };
    return { type: 'fetching' };
  }

  const loadMore = async () => {
    const loaded = page.items.length;
    if (isLoadingMore.current || loaded >= page.filteredCount) return;

    isLoadingMore.current = true;
    try {
      await query.fetchMore({ variables: { offset: loaded } });
    } finally {
      isLoadingMore.current = false;
    }
  };

  const updateTier: PagedInfluencerListModel['updateTier'] = async (p) => {
    await updateTierMutation({
      variables: { data: { id: p.influencerId, tier: p.tier } },
      update: (cache) => writeCreatorTier(cache, p, variables),
    });
  };

  const deleteInfluencer: PagedInfluencerListModel['deleteInfluencer'] = async (
    influencerId,
  ) => {
    await ajax.deleteInfluencer(influencerId);
    removeCreatorFromCache(client.cache, influencerId);
  };

  return toAsyncFetched({
    influencers,
    filteredCount: page.filteredCount,
    totalCount: page.totalCount,
    loadMore,
    updateTier,
    deleteInfluencer,
  });
};
